import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Select } from "antd";
import { useEffect, useRef, useState } from "react";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

import { SELECT_INPUT_FOCUS_DELAY } from "../../constants";
import { useGlobalNewOpportunityModalContext } from "../../context";
import { useMultiSelect } from "../../hooks/useMultiSelect";
import { useGlobalCreateOpportunityStore } from "../../store";
import DDClearIcon from "../DDClearIcon";

import { DefaultDDRenderer } from "./DefaultDDRenderer";
import { MultiSelectLabelTag } from "./MultiSelectLabelTag";

interface Props {
  arrtibuteName: string;
}

export const MultiSelectWithFormItem = ({ arrtibuteName }: Props) => {
  const { form } = useGlobalNewOpportunityModalContext();
  const [searchValue, setSearchValue] = useState("");
  const ref = useRef<any>(null);

  const { attribute, openSelect, setOpenSelect, handleSelect, ensureArray } =
    useMultiSelect(arrtibuteName);
  const { resetSelectTypeAttributes } = useGlobalCreateOpportunityStore();

  const handleDropdownClose = () => {
    setOpenSelect(false);
    // Focus the select after closing
    setTimeout(() => {
      ref.current?.focus();
    }, SELECT_INPUT_FOCUS_DELAY);
  };

  useEffect(() => {
    const filteredOptions = attribute?.options.filter(op => op.active);
    form?.setFieldValue(
      attribute?.property_name,
      filteredOptions?.map(op => op.id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleTagClose = (e: React.MouseEvent, value: any) => {
    // Prevent the click event from bubbling up
    e.preventDefault();
    e.stopPropagation();

    const targetValue = attribute?.options.find(
      contact => contact.id === value,
    );
    if (targetValue) {
      handleSelect(targetValue);
    }
  };

  return (
    <Form.Item
      name={attribute?.property_name}
      label={attribute?.original_name}
      normalize={ensureArray}
      getValueFromEvent={value => ensureArray(value)}
    >
      <Select
        onClick={e => {
          // Check for both the close icon class and SVG path
          const target = e.target as HTMLElement;
          const isCloseIcon =
            target.classList.contains("anticon-close") ||
            target.closest(".anticon-close");

          if (isCloseIcon) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        allowClear={{
          clearIcon: <DDClearIcon />,
        }}
        onClear={() => {
          if (attribute?.property_name) {
            form?.resetFields([attribute?.property_name]);
            resetSelectTypeAttributes(attribute?.property_name);
          }
        }}
        onFocus={() => {
          setOpenSelect(true);
        }}
        placeholder={`Select ${attribute?.current_name}`}
        ref={ref}
        mode="multiple"
        showSearch={false}
        tagRender={e => {
          return (
            <MultiSelectLabelTag
              value={e.value}
              label={e.label}
              onTagClose={handleTagClose}
            />
          );
        }}
        onDropdownVisibleChange={e => {
          setOpenSelect(e);
        }}
        open={openSelect}
        suffixIcon={
          !!attribute?.options?.filter(option => option.active)?.length ? (
            <DDClearIcon asSuffix />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} />
          )
        }
        value={ensureArray(form?.getFieldValue(attribute?.property_name))}
        labelInValue={false}
        labelRender={e => {
          return attribute?.options.find(option => option.id === e.value)
            ?.label;
        }}
        dropdownRender={menu => {
          return (
            <DefaultDDRenderer
              items={attribute?.options ?? []}
              isMultiSelect={true}
              onSearch={setSearchValue}
              onItemSelect={handleSelect}
              handleMultiSelect={handleSelect}
              onCommandKeydown={e => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDropdownClose();
                }
              }}
            />
          );
        }}
      />
    </Form.Item>
  );
};
